import { MMButton } from '@components'
import SelectMethod from '@components/SelectMethod'
import { FakeJetonbankBanks } from 'mmfintech-checkout-commons'
import type { CountryChoice, SelectOptions, UseAdditionalFieldsOutput } from 'mmfintech-checkout-commons/types'
import { tr, translateCountryName } from 'mmfintech-commons'
import { ErrorDisplay, Input } from 'mmfintech-portal-commons'
import { useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import styles from './Checkout.module.scss'

type SelectMethodStepProps = {
  countryCode: string | null
  setCountryCode: (code: string) => void
  countrySelectionVisible: boolean
  paymentMethod: string | null
  setPaymentMethod: (method: string) => void
  jetonBankSpecialCase: boolean
  fieldValues: UseAdditionalFieldsOutput
  onContinue: () => void
  onCancel?: () => void
}

const SelectMethodStep = (props: SelectMethodStepProps) => {
  const {
    countryCode,
    setCountryCode,
    countrySelectionVisible,
    paymentMethod,
    setPaymentMethod,
    jetonBankSpecialCase,
    fieldValues,
    onContinue,
    onCancel
  } = props

  const { checkoutCountries, checkoutPayError, checkoutPayFetching, session, sessionError } = useSelector(
    ({ checkout: { checkoutCountries, checkoutPayError, checkoutPayFetching, session, sessionError } }) => ({
      checkoutCountries,
      checkoutPayError,
      checkoutPayFetching,
      session,
      sessionError
    }),
    shallowEqual
  )

  const bankOptions = useMemo(() => {
    const result: SelectOptions = []

    result.push({
      value: 'JETON',
      label: tr('METADATA.PAYMENT_OPTIONS.JETON', 'Jeton')
    })

    if (Array.isArray(FakeJetonbankBanks?.[countryCode])) {
      FakeJetonbankBanks[countryCode].forEach((bank, index) => {
        result.push({
          value: String(index + 1),
          label: bank.name
        })
      })
    }

    return result
  }, [countryCode])

  return (
    <>
      <div className='mb-3'>
        {countrySelectionVisible ? (
          <Input
            type='select'
            name='countryCode'
            label={tr('CHECKOUT.PAYMENT.COUNTRY', 'Country')}
            placeholder={tr('CHECKOUT.PAYMENT.COUNTRY_PLACEHOLDER', 'Select country')}
            options={checkoutCountries?.map((country: CountryChoice) => ({
              value: country.countryCode,
              label: country.name
            }))}
            value={countryCode}
            onChange={(_name: string, value: string) => setCountryCode(value)}
            hideErrorLine
            required
          />
        ) : (
          <Input
            type='static'
            label={tr('CHECKOUT.PAYMENT.COUNTRY', 'Country')}
            value={translateCountryName(countryCode)}
            hideErrorLine
          />
        )}
      </div>

      <SelectMethod
        className='use-grid'
        method={paymentMethod}
        setMethod={setPaymentMethod}
        specialCase={jetonBankSpecialCase}
        errorVisible={!!countryCode}
      />

      {paymentMethod && jetonBankSpecialCase && (
        <div className='mt-2'>
          <Input
            type='select'
            label={tr('CHECKOUT.PAYMENT.BANK', 'Bank')}
            {...fieldValues.registerInput('bankChoiceId')}
            options={bankOptions}
            required={true}
            hideErrorLine
          />
        </div>
      )}

      <ErrorDisplay error={[sessionError, checkoutPayError]} />

      <div className='buttons'>
        <MMButton
          type='button'
          variant='primary'
          caption={tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')}
          onClick={onContinue}
          loading={checkoutPayFetching}
          disabled={!paymentMethod}
          data-test='button-continue'
          fullWidth
        />

        {Boolean(session?.showCancelButton) && typeof onCancel === 'function' && (
          <MMButton
            type='button'
            variant='secondary'
            caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
            onClick={onCancel}
            disabled={checkoutPayFetching}
            data-test='button-cancel'
            fullWidth
          />
        )}

        {Boolean(session?.returnUrl) && (
          <a className={styles['back-to-merchant']} href={session?.returnUrl} target='_top'>
            &larr; {tr('CHECKOUT.PAYMENT.BACK_TO_MERCHANT', 'Back to merchant')}
          </a>
        )}
      </div>
    </>
  )
}

export default SelectMethodStep
